import React, { useEffect, useMemo, useState } from "react";
import { MarketplaceCards } from "../components/MarketplaceCards/MarketplaceCards";
import { MiniFacesheet, MiniFacesheetContainer } from "../components/MiniFacesheet/MiniFacesheet";
import { Transition } from "../components/Transition/Transition";
import { connect } from "react-redux";
import * as programActions from "../actions/programActions";
import * as interviewActions from "../actions/interviewActions";
import { LoadingBar } from "../components/LoadingComponents/LoadingBar";
import { IneligiblePrograms } from "../components/MarketplaceCards/IneligiblePrograms";
import { first, some } from "lodash-es";
import { isNotNullOrWhitespace } from "../services/stringService";
import { withUser } from "@coverme/identity";
import { isCallCenterUser } from "../services/userService";
import { Link } from "react-router-dom";
import { Birdseed } from "@coverme/elements";
import { getNextUrl } from "../services/marketplaceWorkflowService";
import { getScores } from "../services/fitRiskReliabilityScoreService";
import * as caseCluesActions from "../actions/caseCluesActions";
import * as tagsAction from "../actions/tagsActions";
import { calculateTotalAllowableIncome, getApplicableHouseholds } from "../services/magiServices";

export const MarketplaceComponent = ({
    programs,
    isProgramsLoading,
    encounterId,
    history,
    encounter,
    updateEncounterStatus,
    isHouseholdLoading,
    approveProgram,
    denyProgram,
    showIneligiblePrograms,
    user,
    customer,
    assistanceCategories,
    households,
    notes,
    verifications,
    getCaseClues,
    caseClues,
    getEncounterTag,
}) => {
    const isEncounterLoading = encounterId !== encounter.encounterId;
    const [isLoading, setIsLoading] = useState(true);

    const hasBestFit = assistanceCategories.indexOf("Medicaid Long Term Care") < 0;

    const responsibleParty = encounter.entry.isResponsiblePartyDifferentFromPatient
        ? encounter.entry.responsibleParty
        : encounter.entry.patient;

    /* The code below was added so that we show the MarketplaceLoadingbar when the app initially loads,
However, when a program is approved, it should not show that loading bar and should instead show the
regular loading bar. The code below makes sure that isLoading is set only once when the app loads.
If isLoading get's set later on in the app, it shows a regular loading bar.
*/
    if (isLoading === true) {
        if (isProgramsLoading === false && isEncounterLoading === false && isHouseholdLoading === false) {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (caseClues?.data.length <= 0 && caseClues.isLoading === false) {
            getCaseClues(encounterId);
        }
    }, []);

    useEffect(() => {
        if (encounterId !== encounter.encounterId) {
            return;
        }

        if (encounter.encounterStatus === "Pending") {
            return;
        }

        updateEncounterStatus(encounterId, "Pending");
    }, [encounter]);

    useEffect(() => {
        if (isCallCenterUser(user)) {
            history.push("customerselection");
        }
    }, []);

    useEffect(() => {
        if (programs !== null && programs.length > 0) {
            getEncounterTag(encounterId);
            const approvedProgram = first(programs.filter(x => x.eligibilityStatus.name === "Approved"));
            if (approvedProgram == null) {
                if (
                    programs?.length == 1 &&
                    programs[0].eligibilityDeterminationStatus.name === "Action Complete" &&
                    programs[0].reasonForIneligibility == "Denied"
                ) {
                    history.push(`/interview/${encounterId}/details/dispatch`);
                } else {
                    return;
                }
            } else if (
                isNotNullOrWhitespace(approvedProgram.supplementalInfoTemplate) &&
                approvedProgram.eligibilityDeterminationStatus.name === "Pending Supplemental Information"
            ) {
                history.push(`/interview/${encounterId}/details/supplemental`);
            } else {
                history.push(`/interview/${encounterId}/details/dispatch`);
            }
        }
    }, [programs]);

    function getCardTemplate(program) {
        switch (program.assistanceCategory) {
            case "Medicaid Long Term Care":
                return "LTC";
            case "Banking Partner":
                return "HOSPITAL";
            default:
                return "HOSPITAL";
        }
    }

    const eligibleFormattedPrograms = useMemo(() => {
        return programs
            .filter(x => x.isEligible)
            .map((x, i) => ({
                name: x.programId,
                isBestFit: hasBestFit === true ? i === 0 : false,
                predictedEligibilityStatus: "APPROVED",
                programName: x.name,
                programDetails: x.details,
                offerId: x.offerId,
                requestingUserCanApprove: x.requestingUserCanApprove || x.requiresPostSelectionApproval,
                eligibilityDeterminationResultId: x.eligibilityDeterminationResultId,
                canCardBeDenied: x.canCardBeDenied === true && x.requiresPostSelectionApproval === false,
                cardTemplate: getCardTemplate(x),
                supplementalInfoTemplate: x.supplementalInfoTemplate,
                requiresPostSelectionApproval: x.requiresPostSelectionApproval,
                score: x.score,
                fitScore: x.fitScore,
                riskScore: responsibleParty.riskScore,
                reliabilityScore: x.reliabilityScore,
                totalDiscoveredAssetValue: x.totalDiscoveredAssetValue,
                programCaseClues: x.programCaseClues,
            }));
    }, [programs]);

    const formattedCaseClues = useMemo(() => {
        if (
            eligibleFormattedPrograms != null &&
            eligibleFormattedPrograms.length > 0 &&
            caseClues.isLoading === false
        ) {
            let bestFit = eligibleFormattedPrograms.filter(program => {
                return program.isBestFit;
            });
            if (
                bestFit != null &&
                bestFit[0] != null &&
                bestFit[0]?.programCaseClues != null &&
                bestFit[0]?.programCaseClues?.length > 0
            ) {
                if (caseClues?.data != null && caseClues?.data.length <= 0) {
                    return bestFit[0].programCaseClues;
                }
                return [...caseClues?.data, ...bestFit[0].programCaseClues];
            } else {
                return caseClues?.data;
            }
        }
    }, [caseClues?.data, eligibleFormattedPrograms]);

    const bestFitScores = useMemo(() => {
        if (eligibleFormattedPrograms.length === 0) {
            return {
                overallScore: null,
                riskScore: null,
                reliabilityScore: null,
                fitScore: null,
            };
        }
        let bestFit = eligibleFormattedPrograms.filter(program => {
            return program.isBestFit;
        });
        if (bestFit.length <= 0) {
            return {
                overallScore: null,
                riskScore: null,
                reliabilityScore: null,
                fitScore: null,
            };
        }
        let scores = getScores(bestFit, responsibleParty);
        return {
            overallScore: scores.overallScore,
            fitScore: scores.fitScore,
            reliabilityScore: scores.reliabilityScore,
            riskScore: scores.riskScore,
        };
    }, [eligibleFormattedPrograms, programs]);

    const canUserApproveAnyProgram = useMemo(() => some(programs, x => x.isEligible && x.requestingUserCanApprove), [
        programs,
    ]);

    const ineligiblePrograms = useMemo(() => {
        return programs.filter(x => !x.isEligible);
    }, [programs]);

    const onApprove = (selectedProgram, isCredit) => {
        if (approveProgram == null) {
            console.error("Approve program is not found...");
            return;
        }

        const financialAssistanceCategories = ["Financial Assistance", "Charity Care"];

        if (selectedProgram.programDetails.Title === "Credit") {
            selectedProgram.offerId = "Credit";
        }

        approveProgram(selectedProgram, customer?.data?.customer?.GlobalId).then(result => {
            // check if the program needs any supplemental info? If yes , then navigate to supplemental info page.
            if (isCredit === true) {
                history.push(`/interview/${encounterId}/details/payment`);
            } else {
                const approvedProgram = programs.filter(
                    x => x.eligibilityDeterminationResultId === selectedProgram.eligibilityDeterminationResultId
                )[0];
                let url = getNextUrl(encounterId, encounter, approvedProgram);

                const programsThatNeedVerification = ["Financial Assistance", "Charity Care"];
                if (programsThatNeedVerification.includes(approvedProgram.assistanceCategory)) {
                    url = `/interview/${encounterId}/details/verification?nextUrl=${url}`;
                }

                history.push(url);
            }
        });
    };

    const onDeny = selectedProgram => {
        if (denyProgram) {
            denyProgram(selectedProgram, customer?.data?.customer?.GlobalId).then(() => {
                if (programs?.length == 1) {
                    history.push(`/interview/${encounterId}/details/dispatch`);
                }
            });
        }
    };

    return (
        <>
            <Birdseed>
                <Link to={"/"}>Home</Link>
                <a href="javascript:void(0)">
                    Interview / <strong>Marketplace</strong>
                </a>
            </Birdseed>
            <div className="marketplace">
                <Transition isTransitioning={isLoading}>
                    <LoadingBar isLoading={isProgramsLoading || isHouseholdLoading || isEncounterLoading}>
                        <MiniFacesheetContainer overallScore={bestFitScores.overallScore} />
                        <div className="marketplace-cards">
                            <MarketplaceCards
                                programs={eligibleFormattedPrograms}
                                onApproved={onApprove}
                                onDeclined={onDeny}
                                patient={encounter?.entry?.patient}
                                canUserApproveAnyProgram={canUserApproveAnyProgram}
                                customer={customer}
                                households={households}
                                notes={notes}
                                verifications={verifications}
                                overallScore={bestFitScores.overallScore}
                                fitScore={bestFitScores.fitScore}
                                riskScore={bestFitScores.riskScore}
                                reliabilityScore={bestFitScores.reliabilityScore}
                                caseClues={formattedCaseClues}
                                responsibleParty={responsibleParty}
                            />
                            {showIneligiblePrograms && (
                                <>
                                    <hr />
                                    <IneligiblePrograms programs={ineligiblePrograms} />
                                </>
                            )}
                        </div>
                    </LoadingBar>
                </Transition>
            </div>
        </>
    );
};

export const MarketplaceComponentWithUser = withUser(MarketplaceComponent);

export const MarketplaceContainer = connect(
    (state, ownProps) => {
        const patientId = state.interview?.entry?.patient?.personId;
        let underlyingPerson = null;
        if (patientId) {
            underlyingPerson = state.person.data[patientId];
        }

        return {
            programs: state.programs.data,
            isProgramsLoading: state.programs.isLoading === true || state.programs.isInitialized === false,
            encounter: state.interview,
            encounterId: ownProps.match.params.encounterId,
            isHouseholdLoading: state.households.isInitialized === false || state.households.isLoading === true,
            households: state.households.data,
            assistanceCategories: state.assistanceCategories.data,
            customer: state.customer,
            notes: state.notes.data[underlyingPerson?.globalId],
            verifications: state.interview.verifications,
            caseClues: state.caseClues,
        };
    },
    dispatch => ({
        approveProgram: (program, customerId) => dispatch(programActions.approveProgram(program, customerId)),
        denyProgram: (program, customerId) => dispatch(programActions.denyProgram(program, customerId)),
        updateEncounterStatus: (encounterId, status) =>
            dispatch(interviewActions.updateEncounterStatus(encounterId, status)),
        getCaseClues: encounterId => dispatch(caseCluesActions.getCaseClues(encounterId)),
        getEncounterTag: encounterId => dispatch(tagsAction.getEncounterTag(encounterId)),
    })
)(MarketplaceComponentWithUser);
